import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  Checkbox,
  Button,
  Dropdown,
} from "@swordsweeper/ssi-ui-component-library";
import * as Yup from "yup";
import DistributorAuthMethods, {
  DistributorAuthMethod,
} from "@src/constants/DistributorAuthMethods";
import Distributors, {
  Distributor,
  DISTRIBUTOR_NAMES,
} from "@src/constants/Distributors";
import { DISTRIBUTOR_PATTERSON } from "@src/constants/Distributors";
import { DISTRIBUTOR_MWI } from "@src/constants/Distributors";
import { DISTRIBUTOR_MIDWEST } from "@src/constants/Distributors";
import { DistributorTermsOfService } from "@src/types/termsOfService";

/** Delimiter used to combine billTo and shipTo values for Midwest account dropdown values */
const MIDWEST_ACCOUNT_DELIMITER = "|";

/**
 * Enum representing the steps in the Midwest connection form.
 * Step 1: User enters credentials for validation
 * Step 2: User selects their bill-to/ship-to account
 */
export enum MidwestFormStep {
  ValidateCredentials = 1,
  SelectAccount = 2,
}

export const schema = Yup.object()
  .shape({
    distributor: Yup.string().trim(),
    distributorAuthMethod: Yup.string().trim(),
    username: Yup.string().trim(),
    password: Yup.string().trim(),
    accountId: Yup.string().trim(),
    customerNumber: Yup.string().trim(),
    billToNumber: Yup.string().trim(),
    shipToNumber: Yup.string().trim(),
    userAgreedToTerms: Yup.boolean(),
    requiresTermsAgreement: Yup.boolean().default(false),
    loginId: Yup.string().trim(),
    midwestStep: Yup.number().default(1),
    midwestRefreshToken: Yup.string(),
    midwestCustomers: Yup.array().default([]),
    selectedBillTo: Yup.string().trim(),
    selectedShipTo: Yup.string().trim(),
  })
  .test(
    "auth-method-variations",
    "You must provide either username & password or account ID, billToNumber, & shipToNumber.",
    function (values) {
      const {
        distributor,
        distributorAuthMethod,
        username,
        password,
        accountId,
        customerNumber,
        billToNumber,
        shipToNumber,
        midwestStep,
        midwestRefreshToken,
        selectedBillTo,
      } = values || {};

      let hasUserAuth = false;
      let hasAccountAuth = false;

      if (distributorAuthMethod === DistributorAuthMethods.UsernamePassword) {
        hasUserAuth = !!username && !!password;
        if (hasUserAuth && distributor === DISTRIBUTOR_MWI) {
          // MWI also needs an account ID
          hasUserAuth = !!accountId;
        }
      } else if (distributorAuthMethod === DistributorAuthMethods.Other) {
        if (distributor === DISTRIBUTOR_PATTERSON) {
          hasAccountAuth = !!customerNumber && !!billToNumber && !!shipToNumber;
        }
      } else if (distributorAuthMethod === DistributorAuthMethods.Token) {
        if (distributor === DISTRIBUTOR_MIDWEST) {
          // Step 1: Validate credentials - need loginId and password
          // Step 2: Select account - need refreshToken and selectedBillTo
          if (midwestStep === 1 || !midwestStep) {
            return !!values?.loginId && !!values?.password;
          }
          return !!midwestRefreshToken && !!selectedBillTo;
        }
      }

      return hasUserAuth || hasAccountAuth;
    },
  )
  .test(
    "terms-agreement",
    "You must review and agree to the Terms and Conditions.",
    function (values) {
      const { requiresTermsAgreement, userAgreedToTerms } = values || {};
      if (!requiresTermsAgreement) {
        return true;
      }
      return Boolean(userAgreedToTerms);
    },
  );

/** Parses combined "billTo|shipTo" value into separate components */
export function parseMidwestAccountValue(combinedValue: string): {
  billTo: string;
  shipTo: string;
} {
  const [billTo, shipTo = ""] = (combinedValue || "").split(
    MIDWEST_ACCOUNT_DELIMITER,
  );
  return { billTo: billTo || "", shipTo };
}

/** Creates combined "billTo|shipTo" value from separate components */
export function createMidwestAccountValue(
  billTo: string,
  shipTo: string,
): string {
  return `${billTo}${MIDWEST_ACCOUNT_DELIMITER}${shipTo}`;
}

/** Normalized customer option for the account selection dropdown */
interface MidwestCustomerOption {
  billTo: string;
  billToName: string;
  shipTo: string;
  shipToName: string;
}

/**
 * Ship-to address from Midwest API response.
 */
interface MidwestShipTo {
  shippingNumber?: string;
  customerNumber?: string;
  name?: string;
}

/** Bill-to account from Midwest API response */
interface MidwestBillTo {
  customerNumber?: string;
  name: string;
  shipTos?: MidwestShipTo[];
}

/** Top-level response from Midwest customer list API */
interface MidwestCustomersResponse {
  billTos?: MidwestBillTo[];
}

interface DistributorConnectionFormData {
  username?: string;
  password?: string;
  accountId?: string;
  customerNumber?: string;
  billToNumber?: string;
  shipToNumber?: string;
  userAgreedToTerms?: boolean;
  requiresTermsAgreement?: boolean;
  loginId?: string;
  midwestStep?: number;
  midwestRefreshToken?: string;
  midwestCustomers?: MidwestCustomerOption[];
  selectedBillTo?: string;
  selectedShipTo?: string;
}

interface DistributorConnectionFormProps {
  distributor: Distributor;
  distributorAuthMethod: DistributorAuthMethod;
  currentTerms: DistributorTermsOfService | null;
  userAgreedToTerms: boolean;
  formData: DistributorConnectionFormData;
  onChange: (data: {
    name: keyof DistributorConnectionFormData;
    value: string | boolean | number | MidwestCustomerOption[];
  }) => void;
  setFormData: (data: DistributorConnectionFormData) => void;
}

export default function DistributorConnectionForm({
  distributor,
  distributorAuthMethod,
  currentTerms,
  userAgreedToTerms,
  formData,
  onChange,
  setFormData,
}: DistributorConnectionFormProps) {
  const [hasAgreedTerms, setHasAgreedTerms] = useState<boolean>(
    Boolean(formData.userAgreedToTerms),
  );
  const shouldRequestTerms = Boolean(currentTerms) && !userAgreedToTerms;
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    setHasAgreedTerms(Boolean(formData.userAgreedToTerms));
  }, [formData.userAgreedToTerms]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (formData.requiresTermsAgreement === shouldRequestTerms) return;
    onChangeRef.current({
      name: "requiresTermsAgreement",
      value: shouldRequestTerms,
    });
  }, [formData.requiresTermsAgreement, shouldRequestTerms]);

  const handleTermsChange = (value: boolean) => {
    setHasAgreedTerms(value);
    onChange({ name: "userAgreedToTerms", value });
  };

  if (distributorAuthMethod === DistributorAuthMethods.UsernamePassword) {
    return (
      <>
        {shouldRequestTerms && (
          <>
            <h3 id="distributor-terms-url">
              <a target="_blank" href={currentTerms?.url ?? "#"}>
                {DISTRIBUTOR_NAMES[distributor]} -{" "}
                {currentTerms?.name ?? "Terms and Conditions"}
              </a>
            </h3>
            <Checkbox
              className="checkbox text-sm"
              name="termsAgree"
              checked={hasAgreedTerms}
              required
              onChange={handleTermsChange}
              label={
                <span className="text-sm">
                  I have reviewed and agree to the{" "}
                  {currentTerms?.name ?? "Terms and Conditions"} linked above.
                </span>
              }
              data-testid={`${Distributors.PattersonVeterinary}-terms-checkbox`}
            />
          </>
        )}
        {distributor === DISTRIBUTOR_MWI && (
          <Input
            label="Account ID"
            defaultValue=""
            onChange={(value) => onChange({ name: "accountId", value })}
            value={formData.accountId}
            required
            width="100%"
            data-testid={`${distributor}-account-id-input`}
          />
        )}
        <Input
          label="Username"
          defaultValue=""
          onChange={(value) => onChange({ name: "username", value })}
          value={formData.username}
          required
          width="100%"
          autoComplete={false}
          data-testid={`${distributor}-username-input`}
        />
        <Input
          label="Password"
          defaultValue=""
          onChange={(value) => onChange({ name: "password", value })}
          value={formData.password}
          required
          width="100%"
          type="password"
          autoComplete={false}
          data-testid={`${distributor}-password-input`}
        />
      </>
    );
  }

  if (distributorAuthMethod === DistributorAuthMethods.Other) {
    if (distributor === DISTRIBUTOR_PATTERSON) {
      return (
        <>
          {shouldRequestTerms && (
            <>
              <h3 id="distributor-terms-url">
                <a target="_blank" href={currentTerms?.url ?? "#"}>
                  {DISTRIBUTOR_NAMES[distributor]} -{" "}
                  {currentTerms?.name ?? "Terms and Conditions"}
                </a>
              </h3>
              <Checkbox
                className="checkbox text-sm"
                name="termsAgree"
                checked={hasAgreedTerms}
                onChange={handleTermsChange}
                required
                label={
                  <span className="text-sm">
                    I have reviewed and agree to the{" "}
                    {currentTerms?.name ?? "Terms and Conditions"} linked above.
                  </span>
                }
                data-testid={`${Distributors.PattersonVeterinary}-terms-checkbox`}
              />
            </>
          )}
          <Input
            label="Customer Number"
            defaultValue=""
            onChange={(value) => onChange({ name: "customerNumber", value })}
            value={formData.customerNumber}
            required
            width="100%"
            data-testid={`${Distributors.PattersonVeterinary}-customer-number-input`}
          />
          <Input
            label="Ship To #"
            defaultValue=""
            onChange={(value) => onChange({ name: "shipToNumber", value })}
            value={formData.shipToNumber}
            required
            width="100%"
            data-testid={`${Distributors.PattersonVeterinary}-ship-to-number-input`}
          />
          <Input
            label="Bill To #"
            defaultValue=""
            onChange={(value) => onChange({ name: "billToNumber", value })}
            value={formData.billToNumber}
            required
            width="100%"
            data-testid={`${Distributors.PattersonVeterinary}-bill-to-number-input`}
          />
        </>
      );
    }
  }

  if (distributorAuthMethod === DistributorAuthMethods.Token) {
    if (distributor === DISTRIBUTOR_MIDWEST) {
      return (
        <MidwestConnectionForm
          formData={formData}
          onChange={onChange}
          setFormData={setFormData}
          shouldRequestTerms={shouldRequestTerms}
          currentTerms={currentTerms}
          hasAgreedTerms={hasAgreedTerms}
          handleTermsChange={handleTermsChange}
        />
      );
    }
  }

  return <span>Form not yet implemented</span>;
}

interface MidwestConnectionFormProps {
  formData: DistributorConnectionFormData;
  onChange: (data: {
    name: keyof DistributorConnectionFormData;
    value: string | boolean | number | MidwestCustomerOption[];
  }) => void;
  setFormData: (data: DistributorConnectionFormData) => void;
  shouldRequestTerms: boolean;
  currentTerms: DistributorTermsOfService | null;
  hasAgreedTerms: boolean;
  handleTermsChange: (value: boolean) => void;
}

/**
 * Parses Midwest API customer response into dropdown options for account selection.
 *
 * Includes all bill-to accounts with their ship-to addresses. Ship-to is optional
 * for Midwest orders (the API accepts empty string for ship_to).
 *
 * @param customers - The customer data from the Midwest validate credentials API
 * @returns Array of customer options formatted for the account selection dropdown
 * @throws May throw if customers parameter is malformed (caller should handle)
 */
export function parseMidwestCustomers(
  customers: MidwestCustomersResponse,
): MidwestCustomerOption[] {
  const billTos: MidwestBillTo[] = customers.billTos || [];

  const options: MidwestCustomerOption[] = [];
  for (const billTo of billTos) {
    const shipTos: MidwestShipTo[] = billTo.shipTos || [];
    const customerNumber = billTo.customerNumber || "";

    if (shipTos.length === 0) {
      // Bill-to with no ship-to addresses - ship_to can be empty for Midwest
      options.push({
        billTo: customerNumber,
        billToName: billTo.name,
        shipTo: "",
        shipToName: billTo.name,
      });
    } else {
      // Include all ship-to addresses for this bill-to
      for (const shipTo of shipTos) {
        const shipToNumber = shipTo.shippingNumber || "";
        options.push({
          billTo: customerNumber,
          billToName: billTo.name,
          shipTo: shipToNumber,
          shipToName: shipTo.name || billTo.name,
        });
      }
    }
  }
  return options;
}

function MidwestConnectionForm({
  formData,
  onChange,
  setFormData,
  shouldRequestTerms,
  currentTerms,
  hasAgreedTerms,
  handleTermsChange,
}: MidwestConnectionFormProps) {
  const step = formData.midwestStep || MidwestFormStep.ValidateCredentials;
  const customers: MidwestCustomerOption[] = formData.midwestCustomers || [];

  const handleAccountSelect = (item: { value: string; label: string }) => {
    const { billTo, shipTo } = parseMidwestAccountValue(item.value);
    // Use setFormData to batch update both fields at once
    setFormData({
      ...formData,
      selectedBillTo: billTo,
      selectedShipTo: shipTo,
    });
  };

  const handleBackToStep1 = () => {
    // Use setFormData to batch reset all fields at once
    // Clear password when going back for security
    setFormData({
      ...formData,
      midwestStep: MidwestFormStep.ValidateCredentials,
      midwestRefreshToken: "",
      midwestCustomers: [],
      selectedBillTo: "",
      selectedShipTo: "",
      password: "",
    });
  };

  if (step === MidwestFormStep.ValidateCredentials) {
    return (
      <>
        {shouldRequestTerms && (
          <>
            <h3 id="distributor-terms-url">
              <a target="_blank" href={currentTerms?.url ?? "#"}>
                {DISTRIBUTOR_NAMES[DISTRIBUTOR_MIDWEST]} -{" "}
                {currentTerms?.name ?? "Terms and Conditions"}
              </a>
            </h3>
            <Checkbox
              className="checkbox text-sm"
              name="termsAgree"
              checked={hasAgreedTerms}
              required
              onChange={handleTermsChange}
              label={
                <span className="text-sm">
                  I have reviewed and agree to the{" "}
                  {currentTerms?.name ?? "Terms and Conditions"} linked above.
                </span>
              }
              data-testid={`${Distributors.MidwestVeterinarySupply}-terms-checkbox`}
            />
          </>
        )}
        <Input
          label="Email"
          defaultValue=""
          onChange={(value) => onChange({ name: "loginId", value })}
          value={formData.loginId}
          required
          width="100%"
          type="email"
          data-testid={`${Distributors.MidwestVeterinarySupply}-email-input`}
        />
        <Input
          label="Password"
          defaultValue=""
          onChange={(value) => onChange({ name: "password", value })}
          value={formData.password}
          required
          width="100%"
          type="password"
          data-testid={`${Distributors.MidwestVeterinarySupply}-password-input`}
        />
      </>
    );
  }

  // Step 2: Select account from validated accounts list
  const selectedCustomer = formData.selectedBillTo
    ? customers.find(
        (customer) =>
          customer.billTo === formData.selectedBillTo &&
          customer.shipTo === (formData.selectedShipTo || ""),
      )
    : null;

  return (
    <>
      <label id="account-select-label" className="text-sm margin-bottom-md">
        Select your account to connect:
      </label>
      <Dropdown
        aria-labelledby="account-select-label"
        items={customers.map((customer) => ({
          value: createMidwestAccountValue(customer.billTo, customer.shipTo),
          label: `${customer.billToName} (${customer.billTo})${customer.shipTo ? ` - Ship To: ${customer.shipTo}` : ""}`,
        }))}
        onItemClick={handleAccountSelect}
        selectedLabel={
          selectedCustomer
            ? `${selectedCustomer.billToName} (${selectedCustomer.billTo})${selectedCustomer.shipTo ? ` - Ship To: ${selectedCustomer.shipTo}` : ""}`
            : "Select an account"
        }
        customWidth="100%"
        customHeight="40px"
        dropdownIcon={<i className="fa-sharp fa-regular fa-chevron-down" />}
      />
      <div className="margin-top-md">
        <Button className="btn btn-link text-sm" onClick={handleBackToStep1}>
          Use different credentials
        </Button>
      </div>
    </>
  );
}
